import FridgeImage from "assets/Images/UserFridge.png";
import { PencilIcon } from "components/atoms/Icon";
import Button from "components/atoms/buttons/Button";
import MagnetCard, { MagnetMobileCard } from "components/atoms/cards/user/MagnetAndMementoCard";
import DefaultImage from "assets/Images/BrandImage1.png";
import { useEffect, useRef, useState } from "react";
import HintIconWithTooltip from "components/molecules/forms/HintIconWithTooltip";
import clsx from "clsx";

function Fridge({
  isPublicProfile,
  setFeaturedModalOpen,
  featuredMagnets,
  handleInventoryItemClick,
  isMobile = false,
}: {
  isPublicProfile: boolean;
  setFeaturedModalOpen: (value: boolean) => void;
  featuredMagnets: InventoryItem[];
  handleInventoryItemClick: (id: string) => void;
  isMobile?: boolean;
}) {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  // useRef allows us to "store" the div in a constant,
  // and to access it via observedDiv.current
  const observedDiv = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      if (!observedDiv.current) {
        return undefined;
      }
      const resizeObserver = new ResizeObserver(() => {
        if (observedDiv.current?.offsetWidth !== width) {
          setWidth(observedDiv.current?.offsetWidth || 0);
        }
        if (observedDiv.current?.offsetHeight !== height) {
          setHeight(observedDiv.current?.offsetHeight || 0);
        }
      });
      resizeObserver.observe(observedDiv.current);

      // if useEffect returns a function, it is called right before the
      // component unmounts, so it is the right place to stop observing
      // the div
      return () => resizeObserver.disconnect();
    },
    // only update the effect if the ref element changed
    [observedDiv.current],
  );

  return (
    <div
      ref={observedDiv}
      style={{
        backgroundImage: `url(${FridgeImage})`,
        backgroundSize: "100% 100%",
        padding: `${Math.floor(0.137 * height)}px ${Math.floor(0.0553 * width)}px ${Math.floor(
          0.137 * height,
        )}px ${Math.floor(0.12 * width)}px`,
        gap: `${Math.floor(0.0778 * height)}px ${Math.floor(0.0321 * width)}px`,
      }}
      className="relative grid h-[100%] min-h-[500px] grid-cols-4 grid-rows-3"
    >
      <div
        style={{
          paddingRight: `${Math.floor(0.02 * width)}px`,
          paddingLeft: `${Math.floor(0.06 * width)}px`,
          marginTop: `${Math.floor(0.044 * height)}px`,
          width: `${width}px`,
        }}
        className="absolute flex justify-between"
      >
        <div
          className={clsx("flex font-satoshi text-xxs font-bold text-white 2xl:text-xs", isMobile ? "gap-0" : "gap-1")}
        >
          <span>Fridge</span>
          <HintIconWithTooltip
            hintText="Your Magnets will sit on your user profile, what we affectionately call your Fridge. Mementos can be viewed when you click into your Magnets."
            direction="right"
            isInfo
            hintTextClassName={clsx(isMobile ? "max-w-[15rem]" : "max-w-[20rem]")}
          />
        </div>
        {!isPublicProfile && (
          <Button
            icon={
              <div className="bg-tertiary-btn mr-1 flex h-7 w-7 items-center justify-center rounded-full">
                <PencilIcon className="h-6 w-6 rounded-full bg-secondary p-1" />
              </div>
            }
            onClick={() => setFeaturedModalOpen(true)}
            variant="primary"
            iconLeft
            text="Edit Featured"
            additionalClasses="text-base !py-0 md:text-base lg:text-xxs 2xl:text-xs font-satoshi"
          />
        )}
      </div>
      {featuredMagnets.map((magnet: InventoryItem) =>
        isMobile ? (
          <MagnetMobileCard
            disabled={!magnet.is_purchase_verified}
            isClickable
            key={`featured${magnet.id}_mag`}
            name={magnet.name}
            imageUrl={magnet.image_url || DefaultImage}
            brandName={magnet.brand_name}
            cardAdditionalClasses="magnet-card-background"
            handleOnClick={() => handleInventoryItemClick(magnet.id)}
          />
        ) : (
          <MagnetCard
            isClickable
            disabled={!magnet.is_purchase_verified}
            key={`featured${magnet.id}_mag`}
            name={magnet.name}
            imageUrl={magnet.image_url || DefaultImage}
            brandName={magnet.brand_name}
            cardAdditionalClasses="magnet-card-background"
            handleOnClick={() => handleInventoryItemClick(magnet.id)}
          />
        ),
      )}
    </div>
  );
}

export default Fridge;
